import {
  BaseTool,
  type IToolEnabledInput,
  type ToolButtonComponent,
} from './Tool';
import React, { useEffect, useState } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import { setZoom } from '../utils/zoom';
import { ToolFitToScreen, ToolFitSelectionToScreen } from './ToolFitToScreen';
import ToolButton from '../toolbars/ToolButton';
import ToolFrameNext from './ToolFrameNext';
import ToolFramePrevious from './ToolFramePrevious';
import { ToolMenuItem } from './ToolMenu';
import WhiteboardController from '../controllers/WhiteboardController';
import { DEFAULT_FONT_SIZE } from '../objects/EmbeddedTextBehaviorsV2';
import ToolFrameOpenList from './ToolFrameOpenList';
import useElementRefresh from '../components/hooks/useElementRefresh';

const PresentationFrameMenuItems: React.FC = () => {
  const controller = useController(WhiteboardController);

  return (
    <aha-menu-group>
      <ToolMenuItem tool={ToolFrameOpenList} controller={controller} />
      <ToolMenuItem tool={ToolFrameNext} controller={controller} />
      <ToolMenuItem tool={ToolFramePrevious} controller={controller} />
    </aha-menu-group>
  );
};

const ZoomLevel: ToolButtonComponent = ApplicationView(() => {
  // force rerender when the menu opens to ensure it retrieves fresh state (e.g. tool enabled),
  // as we don't want to have to mirror that into react state
  const { renderKey, elementRef } = useElementRefresh('aha-menu:open', () => {
    controller.actionHideObjectToolbar();
  });

  const controller = useController(WhiteboardController);
  const [zoom, setZoom] = useState(controller.canvas.getZoom());

  // We need to re-render when the zoom changes. We can't get that as a prop,
  // so instead we listen for the event.
  useEffect(() => {
    const setZoomHandler = () => setZoom(controller.canvas.getZoom());
    controller.canvas.on('zoom', setZoomHandler);

    return function cleanup() {
      controller.canvas.off('zoom', setZoomHandler);
    };
  }, [controller.canvas]);

  return (
    <aha-menu ref={elementRef}>
      <ToolButton
        slot='control'
        style={{
          fontSize: DEFAULT_FONT_SIZE,
          userSelect: 'none',
          cursor: 'pointer',
        }}
        testId={ToolZoomLevel.testid()}
        tool={ToolZoomLevel}
      >
        {Math.round(zoom * 100)}%
      </ToolButton>
      <aha-menu-content>
        <PresentationFrameMenuItems key={renderKey} />

        <aha-menu-group>
          <ToolMenuItem tool={ToolFitToScreen} controller={controller} />
          <ToolMenuItem
            key={renderKey}
            tool={ToolFitSelectionToScreen}
            controller={controller}
          />
          {[400, 200, 100, 50, 25, 5].map(zoomLevel => (
            <ToolMenuItem
              key={zoomLevel}
              tool={ToolZoomLevel}
              controller={controller}
              params={{ zoomLevel: zoomLevel / 100.0 }}
            >
              {zoomLevel}%
            </ToolMenuItem>
          ))}
        </aha-menu-group>
      </aha-menu-content>
    </aha-menu>
  );
});

class ToolZoomLevel extends BaseTool {
  static toolName = 'Zoom level';

  static action({
    controller,
    zoomLevel,
  }: {
    controller: WhiteboardController;
    zoomLevel?: number;
  }) {
    setZoom(controller, zoomLevel as number);
  }

  static enabled({ controller }: IToolEnabledInput) {
    return controller.allowPanAndZoom();
  }

  static buttonComponent() {
    return ZoomLevel;
  }
}

export default ToolZoomLevel;
